import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import AppContentView from '../../AppContentView';
// import AppFixedFooter from './AppFixedFooter';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import { useLayoutContext } from '@crema/context/LayoutContextProvider';
import DefaultLayoutWrapper from './DefaultLayoutWrapper';
import MainContent from './MainContent';
import { LayoutType } from '@crema/constants/AppEnums';
import AppSidebar from './AppSidebar';
import DefaultLayoutContainer from './DefaultLayoutContainer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RouterConfigData } from '@crema/models/Apps';
import { useJWTAuth } from '@crema/services/auth/JWTAuthProvider';
import qs from 'qs';
import { LOCAL_STORAGE_NAMES, setLocalStorage } from '@libs/storage/src/lib/local';
import { useAuthUserQuery } from '@libs/services/auth/src/react-query/useAuthUserQuery';
import { AppBar, Toolbar, Typography, useTheme, useMediaQuery, Box } from '@mui/material';
import { useIntl } from 'react-intl';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ROUTES } from '@crema/constants/AppConst';
import { isChannelOnlyMenuPath } from '@crema/helpers';
import { RootState } from '@crema/redux/store';
import DataMigrationBanner from '../components/DataMigrationBanner';
import { useGetTargetUsersQuery } from '@libs/react-query/src/queries/channelUserMigration/useGetTargetUsersQuery';

type Props = {
  routes: React.ReactElement | null;
  routesConfig: RouterConfigData[];
};

const NO_SHOW_FOOTER_PATH = ['/signup', '/signin', '/forget-password'];

// /me api 호출하여 상태 체크
const CHANNEL_PRODUCT_CHECK_PATH = ['/myPod/new', '/product/sales', '/main'];

const DefaultLayout = ({ routes, routesConfig }: Props) => {
  const [searchParams] = useSearchParams();
  const { footer, layoutType, headerType, footerType } = useLayoutContext();
  const { pathname } = useLocation();
  const [isNavCollapsed, setNavCollapsed] = useState(false);
  const { messages } = useIntl();
  const theme = useTheme();
  const domain = process.env.NX_APPLICATION;
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [isDesktopNavCollapsed, setDesktopNavCollapsed] = useState(false);
  const canToggleDesktopNav = domain === 'productmaster';
  const hideDesktopNav = canToggleDesktopNav && isDesktop && isDesktopNavCollapsed;
  const toggleMenu = () => {
    if (canToggleDesktopNav && isDesktop) {
      setDesktopNavCollapsed((collapsed) => !collapsed);
    } else {
      setNavCollapsed((open) => !open);
    }
  };
  const fullsizeSession = sessionStorage.getItem('fullsize');
  const isFullsize = fullsizeSession === 'true';

  const { data: targetUsersData } = useGetTargetUsersQuery(domain || '');
  const toggleNavCollapsed = () => {
    setNavCollapsed((open) => !open);
  };
  const jwtAuthContext = useJWTAuth();
  const navigate = useNavigate();

  const isEditorMode = pathname.indexOf('integratedEditor') !== -1;
  const { data: userAuthData } = useAuthUserQuery();

  const getQueryParams = () => {
    const allQueryParams: any = {};
    searchParams.forEach((value, key) => {
      // @ts-ignore
      allQueryParams[key] = value;
    });

    return allQueryParams;
  };

  const saveBackUrl = () => {
    //로컬 스토리지에 저장
    const allQueryParams: any = getQueryParams();
    const qsStrinfFy = qs.stringify(allQueryParams);
    !pathname.includes('error-pages') &&
      !pathname.includes('blank') &&
      !pathname.includes('myPage') &&
      !isEditorMode &&
      setLocalStorage(LOCAL_STORAGE_NAMES.POD_BACK_URL, `${pathname}${!!qsStrinfFy ? `?${qsStrinfFy}` : ''}`);
  };

  useEffect(() => {
    saveBackUrl();

    if (pathname.startsWith('/myPod')) return;
    if (pathname.startsWith('/editor')) return;
    if (pathname.startsWith('/editor-open')) return;
    if (pathname.startsWith('/product/selase/detail')) return;
    if (searchParams.has('editPodProductId')) return;
    if (isEditorMode) return;

    if (pathname === '/cafe24Auth') return;
    if (pathname.startsWith('/designSync/')) return;
    if (pathname === '/main') return;
    if (pathname === '/d2c-channel-request') return;
    if (pathname.startsWith('/terms/')) return;
    // if (pathname === '/auth') return;

    if (jwtAuthContext?.menus) {
      const matchedMenu = jwtAuthContext?.menus.find((menu: any) => pathname.includes(menu.url));
      if (
        !matchedMenu &&
        // 서버 메뉴에는 없지만 특정 채널에만 여는 화면(AI 메타 검수) — 사이드바 주입과 같은 기준을 본다
        !isChannelOnlyMenuPath(pathname, jwtAuthContext?.user?.channel?.channelId) &&
        !pathname.startsWith('/error-pages') &&
        !pathname.startsWith('/blank') &&
        !pathname.startsWith('/myPage') &&
        !pathname.startsWith('/auth') &&
        !pathname.startsWith(ROUTES.AUTH.SIGNUP_COMPLETE)
      ) {
        navigate('/blank');
      }
    }
  }, [routes]);

  useEffect(() => {
    setNavCollapsed(false);
  }, [pathname]);

  const shouldShowBanner = !!(
    domain === 'ac' &&
    targetUsersData &&
    targetUsersData?.length &&
    targetUsersData?.length > 0
  );

  return (
    <DefaultLayoutContainer
      className={clsx({
        boxedLayout: layoutType === LayoutType.BOXED,
        framedLayout: layoutType === LayoutType.FRAMED,
      })}
    >
      {isEditorMode || pathname.startsWith('/auth') ? (
        routes
      ) : (
        <DefaultLayoutWrapper
          className={clsx('defaultLayoutWrapper', {
            appMainFooter: footer && footerType === 'fluid',
            appMainFixedFooter: footer && footerType === 'fixed',
            appMainFixedHeader: headerType === 'fixed' && !isFullsize,
            'has-banner': shouldShowBanner,
            sidebarCollapsed: hideDesktopNav,
          })}
        >
          {shouldShowBanner && <DataMigrationBanner />}
          {!isFullsize && !hideDesktopNav && (
            <AppSidebar
              routesConfig={routesConfig}
              isNavCollapsed={isNavCollapsed}
              toggleNavCollapsed={toggleNavCollapsed}
            />
          )}

          <MainContent disableSidebarOffset={isFullsize || hideDesktopNav}>
            {!isFullsize && (
              <AppHeader
                toggleNavCollapsed={toggleMenu}
                showDesktopToggle={canToggleDesktopNav}
                isNavExpanded={isDesktop ? !hideDesktopNav : isNavCollapsed}
              />
            )}
            {!isFullsize &&
              process.env.NX_APPLICATION === 'ac' &&
              userAuthData?.channel?.productSuppliedStatus !== 'COMPLETED' && (
                <Toolbar className="product-loading" sx={{ background: theme.palette.primary.main }}>
                  <ErrorOutlineIcon htmlColor="#ffffff" sx={{ marginRight: 2 }} />
                  <Typography variant="h6" component="div" color={'white'}>
                    {messages['channel.product.not.ready'] as string}
                  </Typography>
                </Toolbar>
              )}
            <AppContentView routes={routes} />
            {!NO_SHOW_FOOTER_PATH.includes(pathname) && <AppFooter />}
          </MainContent>
        </DefaultLayoutWrapper>
      )}
    </DefaultLayoutContainer>
  );
};

export default DefaultLayout;
